// Handler factory for transaction CRUD — add, update, delete, duplicate,
// upsert contacts, update client portal, and drag-and-drop status change.
//
// Phase 38 (SAVE-01/SAVE-02): handleAddTransaction and handleUpdateTransaction
// no longer await the primary write, or the contacts/counters/team-split
// syncs, before returning (D-01). The three background syncs run in PARALLEL
// via Promise.allSettled inside run() (D-04: run() still measures the full
// settle for save-duration). A rejected primary write is published to the
// kind: 'primary-write' pending-saves registry entry by attaching directly to
// the write's own promise, bypassing useAsyncHandler.js's run(), which never
// re-throws (D-08). syncPeriodCounters is now LOUD on failure (2 automatic
// retries, then a Retry-toast) and coalesced per workspace (D-05, D-10).
//
// CR-02 fix (38-13-PLAN.md, gap closure): handleAddTransaction/
// handleUpdateTransaction return { ok: true, primaryWritePromise }, NOT a
// bare `true` -- primaryWritePromise is the REAL Firestore write promise.
// Callers that need to know whether the write actually succeeded (not just
// that it was fired) must await primaryWritePromise, never the wrapper
// itself, which always resolves once the write is fired (D-01).
//
// 39-12 (D-13): acquires the bounded, on-demand pricing-period lease
// (usePricingPeriod) HERE -- the save-path site -- NOT at boot. The leased
// Sold-deal set is UPSERTED with this save's own patch (never a naive
// replace) before feeding syncDisplayCounters, so a deal transitioning
// Contract->Sold this save, or one the lease has not yet echoed back, is
// never silently dropped -- see mergePeriodLeaseWithPatch below.
// For an eXp stint, computeOpenDealCommissionUpdates keeps the full
// patched-transactions input unchanged: it reprices Contract/Active deals, a
// scope the D-13 Sold-only period lease structurally does not cover. A plan
// stint gets the merged list, since its open deals price against the
// stint's closed deals in the cap year.

import React from 'react';
import * as transactionService from '../services/transactionService';
import { addActivity } from '../services/activityService';
import * as workspaceService from '../services/workspaceService';
import * as taskService from '../services/taskService';
import { normalizeContactData } from './useContactsData';
import { getContactKey } from '../utils/helpers';
import { isContactCategory, CONSENT_BASIS_TYPES } from '../utils/contactCategory';
import { DEFAULT_EXP_MODULE_SETTINGS } from '../config/workspaceDefaults';
import { syncDisplayCounters } from '../utils/expPeriodUtils';
import { computeOpenDealCommissionUpdates, computePlanPeriodCacheUpdate, pricingPeriodStart, mergePeriodLeaseWithPatch } from '../commissions/pricing';
import { normalizeBrokerage, isExpStint, dealStintId } from '../commissions/brokerage';
import { isSoldStatus } from '../config/statuses';
import { coalesceByKey } from '../utils/coalesceByKey';
import { usePricingPeriod } from '../store/hooks/usePricingPeriod';
import {
    trackCountersSync,
    trackPrimaryWrite,
    resolvePrimaryWrite,
    failPrimaryWrite,
    getSnapshot,
    clear,
} from './usePendingSaves';

const SOLD_STATUS = 'Sold';

// Stable default, so a caller that passes no brokerage is legacy eXp (D-04).
const LEGACY_BROKERAGE = normalizeBrokerage({});

// WR-03-style deterministic-ish save-id generator: unique per save ATTEMPT
// (not per logical change, unlike the activity-log clientRequestId at
// :onActivityClientRequestId below) so each save gets its own independently
// trackable kind: 'primary-write' / kind: 'counters' registry pair
// (${clientRequestId}:write / ${clientRequestId}:counters, per
// usePendingSaves.js's key convention).
let saveRequestSeq = 0;
function nextSaveRequestId(prefix) {
    saveRequestSeq += 1;
    return `${prefix}:${saveRequestSeq}`;
}

// Plan 10 (offline-save honesty): the success toast is TRUE online once the
// write actually acks (CR-05 fix below -- it used to fire immediately, which
// was FALSE whenever the write went on to reject), and TRUE when the browser
// is offline via its own immediate, honest copy -- Firestore's write promise
// does not reject offline, it stays pending and queues the write, and
// src/firebase.js:20 uses a plain getFirestore(app) (a memory-only local
// cache, see TransactionFormV2.js's D-11 comment), so that queued write
// lives only in JS memory and is lost if the tab closes, and no ack is ever
// coming while the tab stays offline. The beforeunload guard (D-11, Plan 08)
// keeps the DATA safe; this fixes the MESSAGING only.
//
// navigator.onLine caveat (documented, not solved -- see 38-10-SUMMARY.md):
// it reports the network INTERFACE, not real reachability. It can report
// `true` behind a captive portal or a dead uplink, in which case the user
// falls onto the online (ack-gated) path below instead of the offline copy
// -- no worse than this plan's starting point, and it correctly catches the
// DevTools-Offline and airplane-mode cases that motivated Plan 10. No
// online/offline event-listener subsystem is introduced -- connectivity is
// read once, at the moment of save, via this one shared helper.
const OFFLINE_SAVE_MESSAGE = "Saved on this device. It will sync when you're back online — don't close this tab until it does.";

// CR-05 fix (38-15-PLAN.md, gap closure, Task 2): human testing caught the
// online success toast firing optimistically, BEFORE the write ever acked
// -- hitting Save on a doomed write showed a green "...successfully!" and
// then, ~310ms later, a red failure toast right behind it. handleAddTransaction/
// handleUpdateTransaction now compute `offline` ONCE at save time and gate
// the deferred (ack-time) success toast on it, so this helper stays the
// single source of truth for the OFFLINE copy+type only; the ONLINE success
// toast is now fired directly by each caller's writePromise.then(), never
// through this helper, so it is never shown before the write genuinely
// resolves.
function isOfflineAtSaveTime() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// I8: the Firestore cache is memory-only, so an offline save lives only in this
// tab until it syncs. The offline message says so once per offline stretch, for
// every save (autosave included), rather than once per save. A save made while
// online ends the stretch; connectivity is still read only at save time.
let offlineStretchAnnounced = false;
function noteOfflineSave(showToastFn, offline) {
    if (!offline) {
        offlineStretchAnnounced = false;
        return;
    }
    if (offlineStretchAnnounced) return;
    offlineStretchAnnounced = true;
    showToastFn(OFFLINE_SAVE_MESSAGE, 'info');
}

function showSaveToast(showToastFn, onlineMessage) {
    if (isOfflineAtSaveTime()) {
        noteOfflineSave(showToastFn, true);
    } else {
        showToastFn(onlineMessage);
    }
}

// D-10: ONE coalescer instance, shared at module scope across every render
// and every hook consumer, so rapid successive saves in the SAME workspace
// coalesce even across re-renders (a per-render instance would defeat this —
// see coalesceByKey.js's header). The wrapped function takes an opaque "job"
// object carrying everything it needs so it never closes over any particular
// hook invocation's props (RESEARCH.md Pitfall 5): each call already builds
// its full job fresh, immediately before calling.
const coalescedCountersSync = coalesceByKey((job) => runCountersSync(job));

// D-13: mergePeriodLeaseWithPatch (src/commissions/pricing.js) upserts the
// bounded period lease with the save's own patched list. It is shared with
// the list and the deal form, which merge the same lease for plan stints.

// The syncPeriodCounters body, unchanged in WHAT it computes (scope lock:
// pricing computation never migrates into this file). Unlike the
// pre-Phase-38 version, failures are NOT swallowed here — they propagate so
// usePendingSaves.js's retry policy (D-05) can retry and, on exhaustion,
// surface a loud Retry toast. syncDeps is threaded through the job so a
// coalesced/retried call keeps using the SAME (possibly test-injected)
// dependencies as its original attempt.
//
// The brokerage decides what is synced. An eXp stint keeps its stored counters,
// walked over only the Sold deals of that stint (for 'legacy-exp' that is every
// unstamped or legacy-stamped deal, so a workspace that never switched syncs
// exactly as before). A plan stint has no stored counters (its cap state is
// derived on read), so it writes no expModuleSettings keys at all. Either way
// the open deals are repriced through computeOpenDealCommissionUpdates.
//
// A plan team leader (teamRole 'team') also keeps brokerage.current.periodCache:
// its team agents' sessions cannot read its deals (firestore.rules), so they
// price its share of their deals against this cache. It is written only by the
// owner (brokerage is owner-only in the rules), only once the team-split
// records have loaded (a cache summed without them would undercount), and only
// when it changed.
async function runCountersSync({ patchedTransactions, periodTransactions, workspaceId, expModuleSettings, workspaceIncome, workspaceIncomeLoaded, canWriteBrokerage, teamRole, onExpModuleSettingsChange, brokerage, syncDeps }) {
    if (!workspaceId || typeof onExpModuleSettingsChange !== 'function') return;
    const b = brokerage?.current ? brokerage : LEGACY_BROKERAGE;
    // D-13: prefer the bounded period lease (decoupled from the active-
    // window/archiveYear boundary) for the Sold-deal pricing computation,
    // upserted with this save's own patch so it is never stale or missing
    // the just-applied change.
    const soldDealsForPricing = mergePeriodLeaseWithPatch(periodTransactions, patchedTransactions);

    if (!isExpStint(b.current)) {
        // A plan prices its open deals against the stint's closed deals in the
        // cap year, which the period lease carries even when they fall outside
        // the active window. The merged list still holds every patched deal.
        // A team leader's team-split records count toward its cap year (spec
        // section 4), exactly as the form and the list count them, so until
        // they load a leader re-prices nothing and refreshes no cache: nets
        // priced without them would be written as if the cap were further off.
        if (teamRole === 'team' && !workspaceIncomeLoaded) return;
        const leaderSplits = teamRole === 'team';
        const teamSplits = leaderSplits ? (workspaceIncome || []).filter(r => r?.source === 'team-split') : [];
        const planUpdates = syncDeps.computeOpenDealCommissionUpdates(
            soldDealsForPricing,
            leaderSplits ? { expModuleSettings, brokerage: b, childRecords: teamSplits } : { expModuleSettings, brokerage: b },
        );
        if (planUpdates.length) {
            await syncDeps.batchUpdateTransactionFields(workspaceId, planUpdates);
        }
        if (leaderSplits && canWriteBrokerage) {
            const cache = syncDeps.computePlanPeriodCacheUpdate(soldDealsForPricing, { brokerage: b, childRecords: teamSplits });
            if (cache) {
                await syncDeps.updateWorkspaceSettings(workspaceId, { 'brokerage.current.periodCache': cache });
            }
        }
        return;
    }

    // A record a plan priced (stamped with a non-eXp stint, from before a
    // switch back to eXp) never counted toward the eXp counters. eXp records
    // are unstamped, so an eXp-only team keeps every record, as before.
    const tawRecords = teamRole === 'team'
        ? (workspaceIncome || []).filter(r => r?.source === 'team-split'
            && (!r.pricingPlan || r.pricingPlan.brokerageId === 'exp'))
        : [];
    // Only this stint's Sold deals count toward its cap. Other deals pass
    // through untouched; syncDisplayCounters reads only Sold deals anyway.
    const stintDeals = soldDealsForPricing.filter(t => !isSoldStatus(t?.status) || dealStintId(t) === b.current.id);
    const counters = syncDeps.syncDisplayCounters(stintDeals, expModuleSettings, tawRecords);
    const updated = { ...expModuleSettings, ...counters };
    // D-55: write ONLY the derived counter keys, as dotted paths, and skip the
    // write when none changed. firestore.rules lets a regular member change
    // just these keys inside expModuleSettings (memberCanSyncExpCounters); the
    // cap settings stay owner-only, so the whole map must never be sent.
    const counterKeys = Object.keys(counters || {});
    const changed = counterKeys.filter((k) => !Object.is(counters[k], expModuleSettings?.[k]));
    if (changed.length) {
        const payload = {};
        for (const k of changed) payload[`expModuleSettings.${k}`] = counters[k];
        await syncDeps.updateWorkspaceSettings(workspaceId, payload);
    }
    onExpModuleSettingsChange(updated);

    // Reprice not-yet-closed deals to their projected capping stage using the
    // freshly-synced counters. Contract-only; idempotent (writes only changed
    // deals). syncPeriodCounters runs from CRUD handlers, never from the
    // Firestore listener, so these writes cannot re-trigger it.
    const projectedUpdates = syncDeps.computeOpenDealCommissionUpdates(patchedTransactions, { expModuleSettings: updated, brokerage: b });
    if (projectedUpdates.length) {
        await syncDeps.batchUpdateTransactionFields(workspaceId, projectedUpdates);
    }
}

// Default (production) implementations of every Firestore-touching operation
// the background chain fans out to. Overridable ONLY via the __syncDeps test
// seam below — no production caller ever passes it. This seam exists because
// this repo's esbuild-bundled tests compile every module export into a
// non-configurable getter (confirmed empirically against this exact file's
// dependencies and tests/mocks/firebase-firestore.js in 38-04-SUMMARY.md),
// so `node:test`'s `t.mock.method` cannot redefine service-layer functions
// directly — the only remaining seam for deterministic, controllable-promise
// testing of "invoked but not yet settled" await topology is an explicit,
// narrow dependency-injection object.
const DEFAULT_SYNC_DEPS = {
    updateTransaction: (...args) => transactionService.updateTransaction(...args),
    createTransaction: (...args) => transactionService.createTransaction(...args),
    createTransactionOnce: (...args) => transactionService.createTransactionOnce(...args),
    updateWorkspaceSettings: (...args) => workspaceService.updateWorkspaceSettings(...args),
    batchUpdateTransactionFields: (...args) => transactionService.batchUpdateTransactionFields(...args),
    upsertContacts: (...args) => workspaceService.upsertContacts(...args),
    upsertTwTeamSplitIncome: (...args) => workspaceService.upsertTwTeamSplitIncome(...args),
    deleteTwTeamSplitIncome: (...args) => workspaceService.deleteTwTeamSplitIncome(...args),
    addActivity: (...args) => addActivity(...args),
    // Pure (non-I/O) commission-counter helpers -- included in the same seam
    // purely so tests can observe exactly which transaction array a given
    // counters attempt ran against (the D-05 x D-10 retry-re-derivation
    // guarantee, T-38-16b). Production always uses the real implementations;
    // WHAT they compute is unchanged (scope lock — no pricing call added).
    syncDisplayCounters: (...args) => syncDisplayCounters(...args),
    computeOpenDealCommissionUpdates: (...args) => computeOpenDealCommissionUpdates(...args),
    computePlanPeriodCacheUpdate: (...args) => computePlanPeriodCacheUpdate(...args),
};

export function useTransactionHandlers({
    selectedWorkspaceId,
    selectedWorkspace,
    run,
    showToast,
    navigateToTransactions,
    allTransactions = [],
    workspaceIncome = [],
    // True once the workspaceIncome lease has delivered its first snapshot.
    // Gates the plan leader's period-cache refresh only.
    workspaceIncomeLoaded = false,
    expModuleSettings = DEFAULT_EXP_MODULE_SETTINGS,
    onExpModuleSettingsChange,
    user,    // { uid, displayName } for activity actor identity (Phase 24)
    // The workspace brokerage (normalizeBrokerage output). Passed in, not read
    // from useBrokerage(): App.js calls this hook above the BrokerageProvider.
    brokerage = LEGACY_BROKERAGE,
    __syncDeps, // test-only override seam — see DEFAULT_SYNC_DEPS above
}) {
    const syncDeps = { ...DEFAULT_SYNC_DEPS, ...__syncDeps };

    // D-05 x D-10 composition (T-38-16b): a retry must re-derive its argument
    // from the FRESHEST transaction list, not replay whatever was captured
    // when the original attempt failed. latestTransactionsRef is updated
    // unconditionally on every hook invocation (every render) so a retry
    // closure captured on an earlier render still reads the current value
    // through this same, stable ref object. This is the one place this file
    // needs React imported.
    const latestTransactionsRef = React.useRef(allTransactions);
    latestTransactionsRef.current = allTransactions;

    // 39-12 (D-13): acquires the bounded, on-demand pricing-period lease
    // HERE, the save-path acquisition site -- NOT at boot. syncDisplayCounters
    // (below, via runCountersSync) always recomputes against TODAY's period
    // (it derives its own periodStart from `new Date()` internally), so this
    // lease is scoped to today's period, independent of any one saved deal's
    // closeDate. A fresh/SSR/no-workspace lease returns an empty array;
    // runCountersSync's mergePeriodLeaseWithPatch falls back to the
    // already-fetched patched transaction list in that case, so this stays a
    // pure ADDITIVE optimization -- never a source of missing pricing data.
    // See src/store/hooks/usePricingPeriod.js for the Critical Pre-Condition
    // (canonicalize-year-statuses) this lease's correctness depends on.
    const todayISODate = new Date().toISOString().slice(0, 10);
    // The lease's start follows the brokerage: eXp keeps its anniversary
    // period; a plan stint's cap year is floored at its join date.
    const periodTransactions = usePricingPeriod(
        expModuleSettings,
        todayISODate,
        selectedWorkspaceId,
        pricingPeriodStart(brokerage, expModuleSettings, todayISODate),
    );
    const latestPeriodTransactionsRef = React.useRef(periodTransactions);
    latestPeriodTransactionsRef.current = periodTransactions;

    // Builds the opaque job the module-scope coalescer/runCountersSync need,
    // fresh per call (never cached) so workspace-level settings are always
    // as-of-this-call — only the transaction ARRAY gets the ref-based
    // freshness treatment (the retry-recompute contract), per plan scope.
    const buildCountersJob = (patchedTransactions) => ({
        patchedTransactions,
        periodTransactions: latestPeriodTransactionsRef.current,
        workspaceId: selectedWorkspaceId,
        expModuleSettings,
        workspaceIncome,
        workspaceIncomeLoaded,
        canWriteBrokerage: Boolean(user?.uid) && selectedWorkspace?.ownerId === user.uid,
        teamRole: selectedWorkspace?.teamRole,
        onExpModuleSettingsChange,
        brokerage,
        syncDeps,
    });

    // Final review F4: after a brokerage switch or an in-place plan edit, the
    // open deals' stored nets must follow the NEW brokerage without waiting for
    // some deal to be saved. The caller hands the new brokerage over explicitly
    // (the write has resolved, but React state does not hold it yet). This runs
    // the same resync a save runs, under the same retry policy, but never
    // through the coalescer: a save in the same window still carries the old
    // brokerage and must not supersede it. A switch to or from eXp therefore
    // keeps eXp's counter handling exactly as a save would run it.
    const resyncOpenDeals = (nextBrokerage) => {
        const target = nextBrokerage?.current ? nextBrokerage : brokerage;
        const clientRequestId = nextSaveRequestId('brokerage');
        return trackCountersSync(clientRequestId, {
            operation: (txs) => runCountersSync({ ...buildCountersJob(txs), brokerage: target }),
            recompute: () => latestTransactionsRef.current,
        }).catch((error) => {
            console.error('Failed to reprice open deals for the new brokerage: ', error);
            showToast('Could not update open deals for the new plan. Saving any deal updates them.', 'error');
        });
    };

    // D-05/D-07: fires a tracked, coalesced, auto-retried counters sync.
    // Never throws — on exhaustion it surfaces a loud, Retry-actioned toast
    // (T-38-18's mitigation) instead of the old swallowed console.error.
    // `builder(txs)` is a pure function producing the full patched array from
    // whatever transaction list it's given; the FIRST attempt evaluates it
    // against latestTransactionsRef.current (identical to allTransactions at
    // call time), retries re-evaluate it against whatever the ref holds by
    // the time they fire (T-38-16b).
    const fireCountersSync = (clientRequestId, builder, transactionId) => {
        const saveId = `${clientRequestId}:counters`;
        // T-38-16b / T-38-20b: attemptWithRetry (usePendingSaves.js) calls
        // `operation` uniformly on every attempt -- it has no notion of
        // "first attempt" vs. "retry". The coalescer/bypass split therefore
        // has to live HERE, as a local flag scoped to this one save: the
        // FIRST call goes through the shared module-scope coalescer (so
        // multiple call sites firing within the same 400ms window collapse
        // into one underlying write, D-10); every call after that (automatic
        // retry OR the manual toast Retry, since both reuse this same
        // `operation` closure via makeRetry) flips to the RAW, uncoalesced
        // runCountersSync -- re-entering the coalescer on retry could let an
        // unrelated newer save's window silently drop this retry's
        // re-derived data (T-38-16b). T-38-20b accepts the resulting
        // duplicate-write cost when several entries fan out from one failed
        // coalesced call and then retry independently and uncoalesced.
        let hasCoalescedOnce = false;
        const operation = (txs) => {
            const job = buildCountersJob(txs);
            if (!hasCoalescedOnce) {
                hasCoalescedOnce = true;
                return coalescedCountersSync(selectedWorkspaceId, job);
            }
            return runCountersSync(job);
        };
        return trackCountersSync(clientRequestId, {
            operation,
            recompute: () => builder(latestTransactionsRef.current),
            transactionId,
        }).catch((error) => {
            console.error('Failed to sync eXp period counters (transaction already saved): ', error);
            showToast('Could not sync commission counters.', 'error', {
                action: {
                    label: 'Retry',
                    onClick: () => {
                        const entry = getSnapshot().get(saveId);
                        // CR-03 fix (38-REVIEW.md): do NOT clear(saveId) here.
                        // makeRetry() (usePendingSaves.js) already manages the
                        // entry's lifecycle correctly on its own -- clear() on
                        // success, re-arm with a fresh retry() on failure. The
                        // pre-fix code cleared synchronously, BEFORE the retry
                        // settled, so a SECOND consecutive failure hit
                        // update()'s no-op-on-missing-entry guard and was
                        // silently swallowed with no further recovery path.
                        // If there's nothing to retry, clearing is still safe
                        // (T-38-14's anti-leak intent) -- there's no in-flight
                        // retry whose own resolution could double-clear it.
                        if (entry?.retry) {
                            entry.retry().catch(() => {
                                // On failure, makeRetry has already re-armed
                                // the entry with a fresh retry() -- a later
                                // save/toast surfaces it. Do not clear here.
                            });
                        } else {
                            clear(saveId);
                        }
                    },
                    // WR-03 fix (38-REVIEW.md, gap closure): cleanup-only
                    // counterpart to onClick, invoked instead of it when
                    // this toast is evicted from ToastContext's 5-slot
                    // stack rather than clicked (ToastContext.js's
                    // evictOldestPreferringActionFree, only reachable once
                    // every visible toast already carries an action).
                    // Deliberately does NOT call entry.retry() -- kicking
                    // off a network retry just because a toast scrolled
                    // off-stack would be a surprising side effect the user
                    // never asked for. If nothing else has reset it, this
                    // is the entry's only remaining path to ever clear
                    // (T-38-14) once its toast is gone.
                    onEvict: () => {
                        clear(saveId);
                    },
                },
            });
        });
    };

    // Writes or removes the TW's team-split income record (and capping
    // counter contribution) when a TAW transaction transitions to/from Sold.
    // For deletes, pass `null` as newTx and the deleted transaction as prevTx.
    // LOUD, unchanged (D-07) — this toast text is preserved verbatim.
    const syncTwTeamSplitIncome = async (newTx, prevTx = null) => {
        if (selectedWorkspace?.teamRole !== 'team_agent') return;
        const twId = selectedWorkspace.parentWorkspaceId;
        if (!twId) return;
        const wasSOLD = prevTx?.status === SOLD_STATUS;
        const isSOLD = newTx?.status === SOLD_STATUS;
        if (!wasSOLD && !isSOLD) return;
        const txId = newTx?.id || prevTx?.id;
        if (!txId) return;
        try {
            if (isSOLD) {
                await syncDeps.upsertTwTeamSplitIncome(twId, selectedWorkspace, { ...newTx, id: txId });
            } else {
                // Scope to this agent: the caller here is the team agent, and
                // firestore.rules only admits a child's team-split list when it
                // carries the matching agentUid clause.
                await syncDeps.deleteTwTeamSplitIncome(twId, txId, selectedWorkspace.ownerId);
            }
        } catch (error) {
            console.error('Failed to sync TW team-split income:', error);
            showToast?.('Could not sync team commission with TW.', 'error');
        }
    };

    // console-only, as today (D-07).
    //
    // withContactFields (Phase 63, D-14, D-17, RESEARCH Pitfall 1): normalizeContactData
    // is a whitelist and drops category, consentBasis, and the stage keys. The create
    // path opts in to carry them, validated; deal paths never pass it (D-16), so a
    // deal-driven upsert of transaction.clients can never write them.
    const upsertWorkspaceContacts = async (contacts = [], { withContactFields = false } = {}) => {
        if (!selectedWorkspaceId || !Array.isArray(contacts) || contacts.length === 0) return;
        const now = Date.now();
        const prepared = contacts
            .filter(Boolean)
            .map(contact => {
                const docId = contact.contactId || contact.id || getContactKey(contact);
                if (!docId) return null;
                const normalized = normalizeContactData(contact, { includeHelpers: true });
                const extra = {};
                if (withContactFields) {
                    if (isContactCategory(contact.category)) extra.category = contact.category;
                    if (
                        contact.consentBasis
                        && typeof contact.consentBasis === 'object'
                        && CONSENT_BASIS_TYPES.includes(contact.consentBasis.type)
                    ) {
                        extra.consentBasis = contact.consentBasis;
                    }
                    for (const key of ['pipelineStage', 'pipelineStageEnteredAt', 'relationshipStatus']) {
                        if (contact[key] !== undefined) extra[key] = contact[key];
                    }
                }
                return {
                    id: docId,
                    contactId: docId,
                    ...normalized,
                    ...extra,
                    createdAt: contact.createdAt || now
                };
            })
            .filter(Boolean);
        if (!prepared.length) return;
        await syncDeps.upsertContacts(selectedWorkspaceId, prepared);
    };

    // `quiet` (autosave): no success or offline toast, and no move to the list,
    // since the form stays open on the deal it just created. A failed write is
    // still tracked in the pending-saves registry; AppRouter decides whether to
    // offer Reopen for it. `id` (autosave): the id the deal's first create
    // minted, so a retry writes the same document.
    const handleAddTransaction = async (incoming, { quiet = false, id: givenId = null } = {}) => {
        if (!selectedWorkspaceId) return false;
        const transaction = incoming;
        const clientRequestId = nextSaveRequestId('add');

        // D-01: fire the primary write; do not await it before returning.
        // createTransaction is non-async (Plan 04) — the id is available
        // synchronously, before any network round-trip.
        const { id, promise: writePromise } = givenId
            ? syncDeps.createTransaction(selectedWorkspaceId, transaction, { id: givenId })
            : syncDeps.createTransaction(selectedWorkspaceId, transaction);
        const created = { ...transaction, id };

        // D-08: publish the write's fate directly off its own promise,
        // bypassing run()'s swallowing catch (useAsyncHandler.js:59-65 never
        // re-throws). kind: 'primary-write' entries never auto-retry —
        // Firestore has already rolled the write back; recovery is the
        // user's own Reopen (Plan 08), not a silent replay.
        trackPrimaryWrite(clientRequestId, transaction, { transactionId: id, quiet });
        // CR-05 fix (38-15-PLAN.md, gap closure, Task 2): read connectivity
        // ONCE, at save time -- never re-derived later, so the online/
        // offline decision for THIS save stays fixed even if the network
        // flips mid-flight. Offline gets its own honest copy immediately
        // below (no ack is ever coming while the tab stays offline); online
        // no longer claims success here -- it waits for the write's real
        // ack in the .then() below, so a write that goes on to reject never
        // shows a green toast first.
        const offlineAtSaveTime = isOfflineAtSaveTime();
        writePromise
            .then(() => {
                resolvePrimaryWrite(clientRequestId);
                if (!offlineAtSaveTime && !quiet) {
                    showToast("Transaction added successfully!");
                }
            })
            .catch((err) => {
                failPrimaryWrite(clientRequestId, err);
                console.error('Primary transaction write rejected (edits retained for reopen): ', err);
                // No success toast on rejection -- AppRouter's always-mounted
                // Reopen watcher (D-08) raises the failure toast itself,
                // keyed off this registry transition to status: 'failed'.
            });

        // D-03: fire immediately, before the background chain settles.
        if (!quiet) navigateToTransactions?.();
        noteOfflineSave(showToast, offlineAtSaveTime);

        // D-04: run() still wraps the FULL settle (write + background chain)
        // so save-duration/the Sentry span stay meaningful — but its promise
        // is deliberately NOT awaited/returned here, so the handler itself
        // settles immediately (D-01). run() can never reject (it swallows
        // and never re-throws), so firing it without a .catch() is safe.
        //
        // The three background syncs run CONCURRENTLY with the primary
        // write, not gated behind its resolution — gating them would make
        // the ":write" and ":counters" registry entries incapable of varying
        // independently (T-38-18b's whole premise: a write that's still
        // in-flight or even destined to fail must not block counters/
        // contacts/team-split from running against the optimistic state).
        run(async () => {
            const contactsWork = upsertWorkspaceContacts(transaction.clients)
                .catch(err => console.error('Contacts sync failed (transaction already saved): ', err));
            const builder = (txs) => [...txs, created];
            const countersWork = fireCountersSync(clientRequestId, builder, id);
            const teamSplitWork = syncTwTeamSplitIncome(created, null);

            await Promise.allSettled([writePromise, contactsWork, countersWork, teamSplitWork]);
        }, "Failed to add transaction.");

        // CR-02 fix (38-REVIEW.md): this promise resolves as soon as the
        // write is FIRED (D-01) -- it is NOT the write's own outcome. The
        // caller (TransactionFormV2.js) must await primaryWritePromise, not
        // this wrapper, to genuinely observe whether the real write
        // succeeded or was rejected. Returning an unconditional boolean here
        // (the pre-fix shape) let backgrounded side effects run even when
        // the write was rejected -- see 38-13-PLAN.md.
        return { ok: true, primaryWritePromise: writePromise, id };
    };

    // Phase 72 (D-18): the headless create the contract setup card runs. Same
    // creation path as the New deal form (the shared payload composition inside
    // createTransactionOnce, contact upserts with the contact fields, the eXp
    // counter sync, and the team-split income sync), but with a deterministic id,
    // create-if-absent, no navigation, no toast, and the write's outcome returned
    // to the caller instead of swallowed: the card owns Retry. The contacts are
    // upserted FIRST (awaited) so the deal's contact rollups find them. Resolves
    // to { id, created }, or null with no workspace or no id. The counter and
    // team-split syncs run only when this call created the deal.
    const addTransactionHeadless = async (incoming, { id, proposalId = null } = {}) => {
        if (!selectedWorkspaceId || !id) return null;
        const transaction = incoming;
        await upsertWorkspaceContacts(transaction.clients, { withContactFields: true });
        const result = await syncDeps.createTransactionOnce(selectedWorkspaceId, transaction, { id, proposalId });
        if (result?.created) {
            const created = { ...transaction, id };
            const clientRequestId = nextSaveRequestId('add-headless');
            const builder = (txs) => [...txs.filter((t) => t.id !== id), created];
            // Same as handleAddTransaction: the counters sync is tracked and retried
            // by the pending-saves registry and never throws, so it is not awaited.
            fireCountersSync(clientRequestId, builder, id);
            await syncTwTeamSplitIncome(created, null);
        }
        return result;
    };

    // `updatedTransaction` may be a partial patch (autosave sends only the
    // changed fields), so everything downstream of the write reads the deal
    // merged over `prev`, never the patch alone. `quiet` mirrors
    // handleAddTransaction.
    const handleUpdateTransaction = async (id, updatedTransaction, previousTransaction = null, { quiet = false } = {}) => {
        if (!selectedWorkspaceId) return false;
        const prev = previousTransaction || allTransactions.find(t => t.id === id) || null;
        const statusChanged = !!(prev && updatedTransaction.status && prev.status !== updatedTransaction.status);
        const basePayload = statusChanged ? { ...updatedTransaction, statusChangedAt: Date.now() } : updatedTransaction;
        const merged = { ...(prev || {}), ...basePayload, id };
        const clientRequestId = nextSaveRequestId(`update:${id}`);

        // D-01: fire the primary write; do not await it before returning.
        // `prev` is passed as previousData (D-04) so a partial payload that
        // touches status without carrying closeDate still derives archiveYear
        // against the deal's real closeDate instead of treating it as missing.
        const writePromise = syncDeps.updateTransaction(selectedWorkspaceId, id, basePayload, prev);

        // D-08: see handleAddTransaction's identical comment above.
        // The registry payload is the whole deal (workspaceStore's overlay reads it as
        // one), while the Firestore write stays the patch.
        trackPrimaryWrite(clientRequestId, merged, { transactionId: id, quiet, isUpdate: true });
        // CR-05 fix (38-15-PLAN.md, gap closure, Task 2): see
        // handleAddTransaction's identical comment above -- read connectivity
        // once, gate the success toast on the write's real ack (online), or
        // show the honest offline copy immediately (offline).
        const offlineAtSaveTime = isOfflineAtSaveTime();
        writePromise
            .then(() => {
                // A quiet success also clears the deal's earlier failed quiet
                // writes (resolvePrimaryWrite): this one carries their edits.
                resolvePrimaryWrite(clientRequestId);
                if (!offlineAtSaveTime && !quiet) {
                    showToast("Transaction updated successfully!");
                }
            })
            .catch((err) => {
                failPrimaryWrite(clientRequestId, err);
                console.error('Primary transaction write rejected (edits retained for reopen): ', err);
                // No success toast on rejection -- AppRouter's always-mounted
                // Reopen watcher (D-08) raises the failure toast itself.
            });

        // D-03: fire immediately, before the background chain settles.
        noteOfflineSave(showToast, offlineAtSaveTime);

        // D-04/T-38-18b: see handleAddTransaction's identical comment above
        // — the background chain runs CONCURRENTLY with the primary write,
        // never gated behind its resolution.
        run(async () => {
            // --- Phase 24 (status-change auto-log), backgrounded alongside
            // the other syncs below. Deterministic per-client clientRequestId
            // (WR-03) preserved verbatim — prevents double-logging on retry
            // or StrictMode double-invoke. console-only (D-07).
            const activityWork = (async () => {
                if (prev && updatedTransaction.status && prev.status !== updatedTransaction.status
                        && Array.isArray(merged.clients)) {
                    for (const client of merged.clients) {
                        if (client.contactId) {
                            try {
                                const onActivityClientRequestId = `status:${id}:${prev.status}->${updatedTransaction.status}:${client.contactId}`;
                                await syncDeps.addActivity({
                                    workspaceId:   selectedWorkspaceId,
                                    contactId:     client.contactId,
                                    type:          'status_change',
                                    summary:       `Status changed to ${updatedTransaction.status}`,
                                    source:        'auto',
                                    createdByUid:  user?.uid || '',
                                    createdByName: user?.displayName || '',
                                    clientRequestId: onActivityClientRequestId,
                                    refs: {
                                        transactionId: id,
                                        fromStatus:    prev.status,
                                        toStatus:      updatedTransaction.status,
                                    },
                                });
                            } catch (actErr) {
                                console.error('Activity log failed (transaction already saved): ', actErr);
                            }
                        }
                    }
                }
            })();

            // A patch without clients leaves the contacts alone.
            const contactsWork = ('clients' in updatedTransaction
                ? upsertWorkspaceContacts(updatedTransaction.clients)
                : Promise.resolve())
                .catch(err => console.error('Contacts sync failed (transaction already saved): ', err));
            const builder = (txs) => txs.map(t => t.id === id ? { ...t, ...basePayload, id } : t);
            const countersWork = fireCountersSync(clientRequestId, builder, id);
            const teamSplitWork = syncTwTeamSplitIncome(merged, prev);

            // allSettled (not all): one failing sync must not prevent the
            // others from completing or from reporting their own outcomes
            // independently, and the (already-fired) primary write's own
            // rejection must not prevent them from running either.
            await Promise.allSettled([writePromise, activityWork, contactsWork, countersWork, teamSplitWork]);
        }, "Failed to update transaction.");

        // CR-02 fix -- see handleAddTransaction's identical comment above.
        return { ok: true, primaryWritePromise: writePromise };
    };

    // W3 mitigation (38-04-SUMMARY.md): createTransaction's { id, promise }
    // shape means `await createTransaction(...)` resolves on the WRAPPER
    // object, not the write — a failed write here was an unhandled promise
    // rejection with no console trace. These two paths are outside
    // SAVE-01/02/03's scope and deliberately do NOT grow a reopen path — no
    // registry tracking, no toast, no retry, just a one-line defensive catch.
    const handleCreateTransaction = async (incoming) => {
        if (!selectedWorkspaceId) return null;
        const transaction = incoming;
        return await run(async () => {
            const docRef = await transactionService.createTransaction(selectedWorkspaceId, transaction);
            docRef.promise.catch(err => console.error('Transaction create write rejected: ', err));
            await upsertWorkspaceContacts(transaction.clients);
            showSaveToast(showToast, "Transaction created successfully!");
            return docRef.id;
        }, "Failed to create transaction.");
    };

    const handleDuplicateTransaction = async (incoming) => {
        if (!selectedWorkspaceId) return false;
        const transaction = incoming;
        return await run(async () => {
            const docRef = await transactionService.createTransaction(selectedWorkspaceId, transaction);
            docRef.promise.catch(err => console.error('Transaction create write rejected: ', err));
            await upsertWorkspaceContacts(transaction.clients);
            return true;
        }, "Failed to duplicate transaction.");
    };

    const handleDeleteTransaction = async (id, transaction = null) => {
        if (!selectedWorkspaceId || !id) return false;
        return await run(async () => {
            const prev = transaction || allTransactions.find(t => t.id === id) || null;
            if (transaction) {
                await transactionService.deleteTransactionWithFiles(selectedWorkspaceId, { ...transaction, id });
            } else {
                await transactionService.deleteTransaction(selectedWorkspaceId, id);
            }
            // Best effort: drop the deal link on its tasks; never blocks the delete.
            await taskService.runTaskCleanupBestEffort('deal', () =>
                taskService.unlinkDealFromTasks({ workspaceId: selectedWorkspaceId, user, transactionId: id }));
            // After delete: re-sync own counters (coalesced, loud, backgrounded
            // — D-05/D-07/D-10 — not awaited so a counters failure/retry chain
            // cannot hold this handler up), and tear down TW's team-split if
            // applicable (unchanged, still awaited).
            const clientRequestId = nextSaveRequestId(`delete:${id}`);
            const builder = (txs) => txs.filter(t => t.id !== id);
            fireCountersSync(clientRequestId, builder, id);
            if (prev) await syncTwTeamSplitIncome(null, { ...prev, id });
            showToast("Transaction deleted.");
            return true;
        }, "Failed to delete transaction.");
    };

    const handleSetAnniversaryMuted = async (transactionId, muted) => {
        if (!selectedWorkspaceId || !transactionId) return false;
        return await run(async () => {
            // D-11: `prev` is passed as previousData so the write chokepoint's
            // rollup recompute (transactionService.js) knows the deal's
            // owning contact(s) and full shape (status/transactionType/
            // closeDate) even though this payload is a partial
            // `{ anniversaryMuted }` update -- otherwise the chokepoint would
            // see no `clients` at all and skip the recompute entirely,
            // leaving the toggled contact's anniversaryCloseDates stale.
            const prev = allTransactions.find(t => t.id === transactionId) || null;
            await transactionService.updateTransaction(selectedWorkspaceId, transactionId, { anniversaryMuted: !!muted }, prev);
            showToast(muted ? 'Anniversary reminders stopped.' : 'Anniversary reminders restored.');
            return true;
        }, 'Failed to update anniversary reminders.');
    };

    const handleTransactionDrop = async (transactionId, newStatus) => {
        if (!selectedWorkspaceId) return;
        await run(async () => {
            const prev = allTransactions.find(t => t.id === transactionId) || null;
            const statusChanged = !!(prev && prev.status !== newStatus);
            // D-04: the kanban drag path -- prev is passed as previousData so
            // this status-only partial payload (no closeDate) still derives
            // archiveYear against the deal's real closeDate, not a missing one.
            await transactionService.updateTransaction(
                selectedWorkspaceId,
                transactionId,
                statusChanged ? { status: newStatus, statusChangedAt: Date.now() } : { status: newStatus },
                prev
            );
            // --- Phase 24 insertion (status-change auto-log) ---
            if (prev && prev.status !== newStatus && Array.isArray(prev.clients)) {
                for (const client of prev.clients) {
                    if (client.contactId) {
                        try {
                            // WR-03: deterministic clientRequestId prevents double-logging on
                            // retry or re-drop to the same column then back.
                            const clientRequestId = `status:${transactionId}:${prev.status}->${newStatus}:${client.contactId}`;
                            await addActivity({
                                workspaceId:   selectedWorkspaceId,
                                contactId:     client.contactId,
                                type:          'status_change',
                                summary:       `Status changed to ${newStatus}`,
                                source:        'auto',
                                createdByUid:  user?.uid || '',
                                createdByName: user?.displayName || '',
                                clientRequestId,
                                refs: {
                                    transactionId: transactionId,
                                    fromStatus:    prev.status,
                                    toStatus:      newStatus,
                                },
                            });
                        } catch (actErr) {
                            console.error('Failed to write status-change activity:', actErr);
                        }
                    }
                }
            }
            // --- end insertion ---
            const saveClientRequestId = nextSaveRequestId(`drop:${transactionId}`);
            const builder = (txs) => txs.map(t => t.id === transactionId ? { ...t, status: newStatus } : t);
            fireCountersSync(saveClientRequestId, builder, transactionId);
            if (prev) {
                await syncTwTeamSplitIncome({ ...prev, status: newStatus, id: transactionId }, prev);
            }
            showToast("Transaction status updated!");
        }, "Failed to update status.");
    };

    return {
        upsertWorkspaceContacts,
        handleAddTransaction,
        addTransactionHeadless,
        handleCreateTransaction,
        handleDuplicateTransaction,
        handleUpdateTransaction,
        handleDeleteTransaction,
        handleSetAnniversaryMuted,
        handleTransactionDrop,
        resyncOpenDeals,
    };
}
